/**
 * KpiCardGrid — responsive flex grid for {@link KpiCard} tiles.
 *
 * Standardises spacing and wrapping so callers do not re-implement the
 * KPI strip layout across SecuritySurfacePanel, AttackSurfaceConvergence,
 * and Governed-K demos.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { KpiCard, type KpiCardProps } from "./KpiCard.js";

/**
 * A KPI tile entry combined with the stable React key the grid will
 * use when reconciling the list. We require a separate `id` rather
 * than reusing `label` because labels are user-visible copy and may
 * collide across tiles (`"Throughput"` for two different metrics) or
 * change between renders, both of which break React reconciliation
 * when the label is also doing duty as the key.
 */
export interface KpiCardGridItem extends KpiCardProps {
  /** Stable identifier for React reconciliation. */
  readonly id: string;
}

export interface KpiCardGridProps {
  /** KPI tiles to render. */
  readonly items: readonly KpiCardGridItem[];
  /** Minimum tile width. Defaults to 180px. */
  readonly minWidth?: number;
  /** Optional accessible label for the wrapping list. */
  readonly ariaLabel?: string;
}

export const KpiCardGrid = React.memo(function KpiCardGrid({
  items,
  minWidth = 180,
  ariaLabel,
}: KpiCardGridProps): React.JSX.Element {
  return (
    <div
      role="list"
      aria-label={ariaLabel}
      data-csk-kpi-grid
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(auto-fit, minmax(${minWidth}px, 1fr))`,
        gap: 12,
      }}
    >
      {items.map(({ id, ...item }) => (
        <div role="listitem" key={id}>
          <KpiCard {...item} />
        </div>
      ))}
    </div>
  );
});
